import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { MainLayout } from '../components/layout/MainLayout';
import { useCustomers } from '../hooks/useCustomers';
import { useCreateSalesOrder } from '../hooks/useSalesOrders';
import { CreateSalesOrderDTO, SalesOrderItem } from '@shared/types/Order';
import { salesOrderService } from '../api/services/salesOrderService';
import { shippingMethodService, ShippingMethod } from '../api/services/shippingMethodService';

interface SalesOrderFormData {
  CustomerID: string;
  CustomerPO: string;
  OrderDate: string;
  ShipByDate: string;
  ShipToName: string;
  ShipToAddress1: string;
  ShipToAddress2: string;
  ShipToCity: string;
  ShipToState: string;
  ShipToZipCode: string;
  ShipToCountry: string;
  ShipVia: string;
  PaymentTerms: string;
  SalesRepID: string;
  IsDropShip: boolean;
  Tax: number;
  Freight: number;
  Discount: number;
  Note: string;
  CustomerEmail: string;
}

export default function InvoiceNew() {
  const navigate = useNavigate();
  const { data: customers, isLoading: customersLoading } = useCustomers();
  const createSalesOrder = useCreateSalesOrder();

  const localDateStr = (d: Date) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  const { register, handleSubmit, watch, setValue, reset } = useForm<SalesOrderFormData>({
    defaultValues: {
      OrderDate: localDateStr(new Date()),
      ShipByDate: localDateStr(new Date()),
      Tax: 0,
      Freight: 0,
      Discount: 0,
      IsDropShip: false,
      ShipToCountry: 'USA',
      CustomerEmail: '',
    },
  });

  const [items, setItems] = useState<Array<SalesOrderItem & { priceStr: string; DiscountPct?: string }>>([
    { PartNumber: '', Description: '', Quantity: 1, Price: 0, priceStr: '0' },
  ]);

  const [shippingMethods, setShippingMethods] = useState<ShippingMethod[]>([]);
  useEffect(() => {
    shippingMethodService.getAllShippingMethods().then(setShippingMethods).catch(() => {});
  }, []);

  const [freightPerItem, setFreightPerItem] = useState(0);
  const [freightOverridden, setFreightOverridden] = useState(false);

  const [customerIdInput, setCustomerIdInput] = useState('');
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [customerDropdownIndex, setCustomerDropdownIndex] = useState(-1);
  const customerIdContainerRef = useRef<HTMLDivElement>(null);

  const selectedCustomerId = watch('CustomerID');

  const filteredCustomers = customerIdInput.trim().length > 0 && customers
    ? customers.filter(c => c.CustomerID.toLowerCase().includes(customerIdInput.toLowerCase())).slice(0, 25)
    : [];

  // Sync Customer ID text box when customer is chosen from the Name dropdown
  useEffect(() => {
    if (selectedCustomerId) setCustomerIdInput(selectedCustomerId);
  }, [selectedCustomerId]);

  // Close Customer ID dropdown on outside click
  useEffect(() => {
    const handle = (e: MouseEvent) => {
      if (customerIdContainerRef.current && !customerIdContainerRef.current.contains(e.target as Node)) {
        setShowCustomerDropdown(false);
      }
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, []);

  // Auto-set ShipByDate to 7 business days from OrderDate
  const orderDate = watch('OrderDate');
  useEffect(() => {
    if (!orderDate) return;
    const date = new Date(orderDate + 'T00:00:00');
    let count = 0;
    while (count < 7) {
      date.setDate(date.getDate() + 1);
      const day = date.getDay();
      if (day !== 0 && day !== 6) count++;
    }
    setValue('ShipByDate', localDateStr(date));
  }, [orderDate, setValue]);

  // Add/remove DS-1 line item when Drop Ship is toggled
  const isDropShip = watch('IsDropShip');
  useEffect(() => {
    if (isDropShip) {
      salesOrderService.lookupPart('DS-1', selectedCustomerId || undefined).then(part => {
        setItems(prev => {
          if (prev.some(i => i.PartNumber === 'DS-1')) return prev;
          return [...prev, {
            PartNumber: 'DS-1',
            Description: part?.Description || '',
            Quantity: 1,
            Price: part?.Price ?? 0,
            priceStr: String(part?.Price ?? 0),
            LineItemIndex: part?.LineItemIndex ?? 0,
          }];
        });
      });
    } else {
      setItems(prev => prev.filter(i => i.PartNumber !== 'DS-1'));
    }
  }, [isDropShip]); // eslint-disable-line react-hooks/exhaustive-deps

  // Track previous customer so we can detect a real change (not initial load)
  const prevCustomerIdRef = useRef('');
  const itemsRef = useRef(items);
  useEffect(() => { itemsRef.current = items; }, [items]);

  // Re-fetch prices for all filled line items when customer changes
  useEffect(() => {
    const prev = prevCustomerIdRef.current;
    prevCustomerIdRef.current = selectedCustomerId || '';
    if (!prev || !selectedCustomerId || prev === selectedCustomerId) return;

    const filledItems = itemsRef.current.filter(i => i.PartNumber.trim());
    if (filledItems.length === 0) return;

    Promise.all(
      filledItems.map(item =>
        salesOrderService.lookupPart(item.PartNumber, selectedCustomerId)
          .then(part => ({ PartNumber: item.PartNumber, Price: part?.Price ?? 0, LineItemIndex: part?.LineItemIndex ?? 0 }))
          .catch(() => ({ PartNumber: item.PartNumber, Price: 0, LineItemIndex: 0 }))
      )
    ).then(updates => {
      setItems(prev => prev.map(item => {
        const update = updates.find(u => u.PartNumber === item.PartNumber);
        return update && update.Price > 0 ? { ...item, Price: update.Price, priceStr: String(update.Price), LineItemIndex: update.LineItemIndex ?? item.LineItemIndex } : item;
      }));
    });
  }, [selectedCustomerId]);

  // Auto-fill shipping address when customer is selected
  useEffect(() => {
    if (selectedCustomerId && customers) {
      const customer = customers.find((c) => c.CustomerID === selectedCustomerId);
      if (customer) {
        setValue('ShipToName', customer.Name || '');
        setValue('ShipToAddress1', customer.BillToAddress1 || '');
        setValue('ShipToAddress2', customer.BillToAddress2 || '');
        setValue('ShipToCity', customer.BillToCity || '');
        setValue('ShipToState', customer.BillToState || '');
        setValue('ShipToZipCode', customer.BillToZipCode || '');
        setValue('ShipVia', customer.ShipVia || '');
        setValue('PaymentTerms', customer.PaymentTerms || '');
        setValue('SalesRepID', customer.SalesRepID || '');
        setFreightPerItem(customer.FreightPerItem ?? 0);
        setFreightOverridden(false);
      }
    }
  }, [selectedCustomerId, customers, setValue]);

  // Auto-calculate Freight = freightPerItem × number of product line items (skips if user manually edited)
  useEffect(() => {
    if (freightPerItem <= 0 || freightOverridden) return;
    const noFreightParts = ['DS-1', 'DISCOUNT', 'SAMPLE', 'MISC-UNIVERSAL'];
    const productCount = items.filter(i => i.PartNumber.trim() && !noFreightParts.includes(i.PartNumber.trim().toUpperCase())).length;
    setValue('Freight', parseFloat((freightPerItem * productCount).toFixed(2)));
  }, [items, freightPerItem, freightOverridden, setValue]);

  const addLineItem = () => {
    setItems([...items, { PartNumber: '', Description: '', Quantity: 1, Price: 0, priceStr: '0' }]);
  };

  const removeLineItem = (index: number) => {
    if (items.length > 1) {
      setItems(items.filter((_, i) => i !== index));
    }
  };

  const updateLineItem = (index: number, field: keyof SalesOrderItem, value: any) => {
    const newItems = [...items];
    newItems[index] = { ...newItems[index], [field]: value };
    setItems(newItems);
  };

  const handleDiscountPctChange = (index: number, pctStr: string) => {
    setItems(prev => {
      const pct = parseFloat(pctStr) || 0;
      const base = prev.reduce((sum, item) =>
        item.PartNumber.trim().toUpperCase() === 'DISCOUNT'
          ? sum
          : sum + item.Quantity * item.Price,
        0);
      const discountAmount = -(base * pct / 100);
      const next = [...prev];
      next[index] = {
        ...next[index],
        DiscountPct: pctStr,
        Price: pct > 0 ? discountAmount : 0,
        priceStr: pct > 0 ? discountAmount.toFixed(2) : '0',
      };
      return next;
    });
  };

  const handlePartNumberBlur = async (index: number) => {
    const raw = items[index].PartNumber.trim();
    if (!raw) return;
    const upper = raw.toUpperCase();
    updateLineItem(index, 'PartNumber', upper);

    if (upper === 'DISCOUNT') {
      const part = await salesOrderService.lookupPart(upper, selectedCustomerId || undefined);
      setItems(prev => {
        const next = [...prev];
        next[index] = {
          ...next[index],
          PartNumber: 'DISCOUNT',
          Description: next[index].Description || 'Discount',
          DiscountPct: next[index].DiscountPct ?? '',
          Price: next[index].Price,
          priceStr: next[index].priceStr,
          LineItemIndex: part?.LineItemIndex ?? next[index].LineItemIndex,
        };
        return next;
      });
      return;
    }

    const part = await salesOrderService.lookupPart(upper, selectedCustomerId || undefined);
    if (!part) return;
    setItems(prev => {
      const next = [...prev];
      const newPrice = part.Price > 0 ? part.Price : next[index].Price;
      next[index] = {
        ...next[index],
        Description: part.Description || next[index].Description,
        Price: newPrice,
        priceStr: part.Price > 0 ? String(part.Price) : next[index].priceStr,
        LineItemIndex: part.LineItemIndex ?? next[index].LineItemIndex,
      };
      return next;
    });
  };

  // Calculate totals
  const subtotal = items.reduce((sum, item) => sum + item.Quantity * item.Price, 0);
  const tax = Number(watch('Tax')) || 0;
  const freight = Number(watch('Freight')) || 0;
  const total = subtotal + tax + freight;

  const onSubmit = async (data: SalesOrderFormData) => {
    const orderData: CreateSalesOrderDTO = {
      ...data,
      Items: items,
      Total: total,
    };

    try {
      const result = await createSalesOrder.mutateAsync(orderData);
      reset();
      setCustomerIdInput('');
      setItems([{ PartNumber: '', Description: '', Quantity: 1, Price: 0, priceStr: '0' }]);
      navigate(`/orders/check/${result.InvoiceNumber || result.OrderID}`);
    } catch (error) {
      console.error('Failed to create order:', error);
    }
  };

  // Get selected customer details
  const selectedCustomer = selectedCustomerId && customers
    ? customers.find((c) => c.CustomerID === selectedCustomerId)
    : null;

  return (
    <MainLayout>
      <div className="p-6" style={{ fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif' }}>
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">New Sales Order</h1>
          <p className="text-sm text-gray-600 dark:text-gray-400">Create a new sales order</p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Customer Selection */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Customer ID *
                </label>
                <div ref={customerIdContainerRef} className="relative">
                  <input type="hidden" {...register('CustomerID', { required: true })} />
                  <input
                    type="text"
                    value={customerIdInput}
                    autoComplete="off"
                    placeholder="Type to search..."
                    onChange={(e) => {
                      const val = e.target.value.toUpperCase();
                      setCustomerIdInput(val);
                      setValue('CustomerID', '');
                      setShowCustomerDropdown(true);
                      setCustomerDropdownIndex(-1);
                    }}
                    onFocus={() => { if (customerIdInput.trim()) setShowCustomerDropdown(true); }}
                    onKeyDown={(e) => {
                      if (e.key === 'ArrowDown') {
                        e.preventDefault();
                        setCustomerDropdownIndex(i => Math.min(i + 1, filteredCustomers.length - 1));
                      } else if (e.key === 'ArrowUp') {
                        e.preventDefault();
                        setCustomerDropdownIndex(i => Math.max(i - 1, -1));
                      } else if (e.key === 'Enter' && customerDropdownIndex >= 0) {
                        e.preventDefault();
                        const c = filteredCustomers[customerDropdownIndex];
                        setCustomerIdInput(c.CustomerID);
                        setValue('CustomerID', c.CustomerID);
                        setShowCustomerDropdown(false);
                      } else if (e.key === 'Escape') {
                        setShowCustomerDropdown(false);
                      }
                    }}
                    onDoubleClick={(e) => {
                      const val = (e.target as HTMLInputElement).value;
                      if (val) navigator.clipboard.writeText(val);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                  {showCustomerDropdown && filteredCustomers.length > 0 && (
                    <ul className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-52 overflow-y-auto">
                      {filteredCustomers.map((c, i) => (
                        <li
                          key={c.CustomerID}
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => {
                            setCustomerIdInput(c.CustomerID);
                            setValue('CustomerID', c.CustomerID);
                            setShowCustomerDropdown(false);
                          }}
                          className={`px-3 py-2 cursor-pointer text-sm ${
                            i === customerDropdownIndex
                              ? 'bg-blue-100 dark:bg-blue-900 text-gray-900 dark:text-white'
                              : 'text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700'
                          }`}
                        >
                          <span className="font-medium">{c.CustomerID}</span>
                          {c.Name && <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">{c.Name}</span>}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Customer Name *
                </label>
                <select
                  value={selectedCustomerId || ''}
                  onChange={(e) => setValue('CustomerID', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  disabled={customersLoading}
                >
                  <option value="">Select Customer Name</option>
                  {customers?.map((customer) => (
                    <option key={customer.CustomerID} value={customer.CustomerID}>
                      {customer.Name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Bill To */}
              <div>
                <h3 className="text-md font-semibold text-gray-900 dark:text-white mb-3">Bill To</h3>
                {selectedCustomer ? (
                  <div className="text-sm text-gray-700 dark:text-gray-300 space-y-1">
                    <div>{selectedCustomer.Name}</div>
                    <div>{selectedCustomer.BillToAddress1}</div>
                    {selectedCustomer.BillToAddress2 && <div>{selectedCustomer.BillToAddress2}</div>}
                    <div>
                      {selectedCustomer.BillToCity}
                      {selectedCustomer.BillToCity && ', '}
                      {selectedCustomer.BillToState}{' '}
                      {selectedCustomer.BillToZipCode}
                    </div>
                  </div>
                ) : (
                  <div className="text-sm text-gray-400 dark:text-gray-500">
                    Select a customer to view billing information
                  </div>
                )}
                <input
                  type="email"
                  {...register('CustomerEmail')}
                  placeholder="Customer email"
                  className="mt-2 w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                />
              </div>

              {/* Ship To */}
              <div>
                <div className="flex justify-between items-center mb-3">
                  <h3 className="text-md font-semibold text-gray-900 dark:text-white">Ship To</h3>
                  <div className="flex items-center space-x-4">
                    <div className="flex items-center space-x-2">
                      <label className="text-sm text-gray-700 dark:text-gray-300">Date:</label>
                      <input
                        type="date"
                        {...register('OrderDate', { required: true })}
                        className="px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                      />
                    </div>
                    <div className="flex items-center space-x-2">
                      <label className="text-sm text-gray-700 dark:text-gray-300">Ship by:</label>
                      <input
                        type="date"
                        {...register('ShipByDate', { required: true })}
                        className="px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                      />
                    </div>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        {...register('IsDropShip')}
                        className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                      />
                      <label className="ml-2 text-sm text-gray-700 dark:text-gray-300">Drop ship</label>
                    </div>
                  </div>
                </div>
                <div className="space-y-2">
                  <input
                    type="text"
                    {...register('ShipToName')}
                    placeholder="Customer"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                  />
                  <input
                    type="text"
                    {...register('ShipToAddress1')}
                    placeholder="Address"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                  />
                  <input
                    type="text"
                    {...register('ShipToAddress2')}
                    placeholder="Address Line 2"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                  />
                  <div className="grid grid-cols-12 gap-2">
                    <input
                      type="text"
                      {...register('ShipToCity')}
                      placeholder="City"
                      className="col-span-6 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                    />
                    <input
                      type="text"
                      {...register('ShipToState')}
                      placeholder="State"
                      maxLength={2}
                      className="col-span-3 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                    />
                    <input
                      type="text"
                      {...register('ShipToZipCode')}
                      placeholder="Zip"
                      className="col-span-3 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                    />
                  </div>
                  <input
                    type="text"
                    {...register('ShipToCountry')}
                    placeholder="Country"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Order Details */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Order Details</h2>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Customer PO
                </label>
                <input
                  type="text"
                  {...register('CustomerPO')}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Ship Via
                </label>
                <select
                  {...register('ShipVia')}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="">Select shipping method</option>
                  {shippingMethods.map((sm) => (
                    <option key={sm.Id} value={sm.Name}>{sm.Name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Terms
                </label>
                <input
                  type="text"
                  {...register('PaymentTerms')}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Sales Rep ID
                </label>
                <input
                  type="text"
                  {...register('SalesRepID')}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
            </div>
          </div>

          {/* Line Items */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Line Items</h2>
              <button
                type="button"
                onClick={addLineItem}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Add Line Item
              </button>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-900">
                  <tr>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase w-24">
                      Quantity
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase w-36">
                      Item
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                      Description
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase w-32">
                      Unit Price
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase w-32">
                      Amount
                    </th>
                    <th className="px-3 py-3 w-20"></th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {items.map((item, index) => (
                    <tr key={index}>
                      <td className="px-3 py-2">
                        <input
                          type="number"
                          value={item.Quantity}
                          onChange={(e) => updateLineItem(index, 'Quantity', parseInt(e.target.value) || 0)}
                          min="1"
                          className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                          required
                        />
                      </td>
                      <td className="px-3 py-2">
                        <input
                          type="text"
                          value={item.PartNumber}
                          onChange={(e) => updateLineItem(index, 'PartNumber', e.target.value.toUpperCase())}
                          onBlur={() => handlePartNumberBlur(index)}
                          className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                          required
                        />
                      </td>
                      <td className="px-3 py-2">
                        <textarea
                          value={item.Description}
                          onChange={(e) => updateLineItem(index, 'Description', e.target.value)}
                          rows={3}
                          maxLength={160}
                          className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm resize-y"
                          required
                        />
                      </td>
                      <td className="px-3 py-2">
                        {item.PartNumber.trim().toUpperCase() === 'DISCOUNT' ? (
                          <div className="flex flex-col items-end gap-1">
                            <div className="flex items-center gap-1">
                              <input
                                type="number"
                                min="0"
                                max="100"
                                step="0.01"
                                value={item.DiscountPct ?? ''}
                                onChange={(e) => handleDiscountPctChange(index, e.target.value)}
                                className="w-20 px-2 py-1 border border-blue-400 dark:border-blue-500 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm text-right"
                                placeholder="0"
                              />
                              <span className="text-gray-500 dark:text-gray-400 text-sm">%</span>
                            </div>
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                              = ${(parseFloat(item.priceStr) || 0).toFixed(2)}
                            </span>
                          </div>
                        ) : (
                          <input
                            type="text"
                            inputMode="decimal"
                            value={item.priceStr}
                            onChange={(e) => {
                              const raw = e.target.value;
                              const num = parseFloat(raw);
                              setItems(prev => {
                                const next = [...prev];
                                next[index] = { ...next[index], priceStr: raw, Price: isNaN(num) ? next[index].Price : num };
                                return next;
                              });
                            }}
                            className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                            required
                          />
                        )}
                      </td>
                      <td className="px-3 py-2 text-sm text-gray-900 dark:text-white">
                        ${(item.Quantity * item.Price).toFixed(2)}
                      </td>
                      <td className="px-3 py-2">
                        {items.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeLineItem(index)}
                            className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                          >
                            Remove
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Totals */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
            {/* Freight row */}
            <div className="flex items-center justify-between px-4 py-2 border-b border-gray-200 dark:border-gray-700">
              <span className="text-sm text-gray-700 dark:text-gray-300">
                Freight:
                {freightOverridden && (
                  <button
                    type="button"
                    onClick={() => setFreightOverridden(false)}
                    className="ml-2 text-xs text-blue-500 hover:text-blue-700 underline"
                  >
                    reset to auto
                  </button>
                )}
              </span>
              <input
                type="number"
                {...register('Freight')}
                onChange={(e) => { setFreightOverridden(true); register('Freight').onChange(e); }}
                step="0.01"
                min="0"
                className="w-48 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm text-right"
              />
            </div>
            {/* Tax row */}
            <div className="flex items-center justify-between px-4 py-2 border-b border-gray-200 dark:border-gray-700">
              <span className="text-sm text-gray-700 dark:text-gray-300">Tax:</span>
              <input
                type="number"
                {...register('Tax')}
                step="0.01"
                min="0"
                className="w-48 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm text-right"
              />
            </div>
            {/* Summary rows */}
            <div className="flex items-center justify-between px-4 py-2 border-b border-gray-200 dark:border-gray-700">
              <span className="text-sm text-gray-600 dark:text-gray-400">Subtotal:</span>
              <span className="text-sm text-gray-900 dark:text-white w-48 text-right">${subtotal.toFixed(2)}</span>
            </div>
            <div className="flex items-center justify-between px-4 py-2 border-b border-gray-200 dark:border-gray-700">
              <span className="text-sm text-gray-600 dark:text-gray-400">Freight:</span>
              <span className="text-sm text-gray-900 dark:text-white w-48 text-right">${freight.toFixed(2)}</span>
            </div>
            <div className="flex items-center justify-between px-4 py-2 border-b border-gray-200 dark:border-gray-700">
              <span className="text-sm text-gray-600 dark:text-gray-400">Tax:</span>
              <span className="text-sm text-gray-900 dark:text-white w-48 text-right">${tax.toFixed(2)}</span>
            </div>
            <div className="flex items-center justify-between px-4 py-3">
              <span className="text-base font-bold text-gray-900 dark:text-white">Total:</span>
              <span className="text-base font-bold text-gray-900 dark:text-white w-48 text-right">${total.toFixed(2)}</span>
            </div>
          </div>

          {/* Notes */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Notes</h2>
            <textarea
              {...register('Note')}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              placeholder="Enter any notes or special instructions..."
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-4">
            <button
              type="button"
              onClick={() => navigate('/orders')}
              className="px-6 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={createSalesOrder.isPending}
              className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {createSalesOrder.isPending ? 'Saving...' : 'Save Order'}
            </button>
          </div>
        </form>
      </div>
    </MainLayout>
  );
}
